// ============================================================================
//  SUPPORT ADMIN TAB

// ============================================================================
// Admin interface for managing support tickets
// Add this as a new tab in AdminDashboard.tsx
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Input,
  Select,
  Textarea,
  Spinner,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  useToast,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Switch,
} from '@chakra-ui/react';
import {
  MessageCircle,
  Clock,
  User,
  Mail,
  AlertTriangle,
  CheckCircle,
  Send,
  RefreshCw,
  Filter,
  Search,
  ArrowLeft,
  MoreVertical,
} from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import PokerCard from '../ui/PokerCard';
import supportService, {
  SupportTicket,
  TicketMessage,
  TicketStatus,
  TicketPriority,
  TicketStats,
  TICKET_CATEGORIES,
  TICKET_STATUSES,
  TICKET_PRIORITIES,
} from '../../services/supportService';

// ============================================================================
// QUICK REPLIES
// ============================================================================

const QUICK_REPLIES = [
  {
    label: 'Greeting',
    message: "Hi there! Thanks for reaching out. I'm looking into this for you now.",
  },
  {
    label: 'Need Info',
    message: "To help you better, could you please provide more details about the issue you're experiencing?",
  },
  {
    label: 'Checking',
    message: "I'm checking on this now. I'll get back to you shortly with an update.",
  },
  {
    label: 'Resolved',
    message: "This issue has been resolved. Please let me know if you need any further assistance!",
  },
  {
    label: 'KYC Info',
    message: "For withdrawals over $50, KYC verification is required. Please go to Profile → Verification to submit your documents.",
  },
  {
    label: 'Withdrawal Time',
    message: "Withdrawals typically process within 24-48 hours. If it's been longer, please let me know and I'll investigate.",
  },
];

// ============================================================================
// PROPS
// ============================================================================

interface SupportAdminTabProps {
  adminId: string;
  adminName: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

const SupportAdminTab: React.FC<SupportAdminTabProps> = ({ adminId, adminName }) => {
  const toast = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // State
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Selected ticket
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [showQuickReplies, setShowQuickReplies] = useState(false);

  // Mobile view state
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');

  // ============================================================================
  // LOAD TICKETS
  // ============================================================================

  useEffect(() => {
    const unsubscribe = supportService.subscribeToAllTickets((updatedTickets) => {
      setTickets(updatedTickets);
      setLoading(false);

      // Update selected ticket if viewing
      if (selectedTicket) {
        const updated = updatedTickets.find(t => t.id === selectedTicket.id);
        if (updated) setSelectedTicket(updated);
      }
    });

    // Load stats
    loadStats();

    return () => unsubscribe();
  }, []);

  const loadStats = async () => {
    try {
      const ticketStats = await supportService.getTicketStats();
      setStats(ticketStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // ============================================================================
  // LOAD MESSAGES
  // ============================================================================

  useEffect(() => {
    if (!selectedTicket) return;

    const unsubscribe = supportService.subscribeToMessages(
      selectedTicket.id,
      (updatedMessages) => {
        setMessages(updatedMessages);
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    );

    // Mark as read
    supportService.markMessagesAsRead(selectedTicket.id, true);

    return () => unsubscribe();
  }, [selectedTicket?.id]);

  // ============================================================================
  // SEND MESSAGE
  // ============================================================================

  const handleSendMessage = async () => {
    if (!selectedTicket || !newMessage.trim()) return;

    setSending(true);

    try {
      await supportService.sendMessage(
        selectedTicket.id,
        adminId,
        adminName,
        newMessage.trim(),
        true // isAdmin
      );

      setNewMessage('');
      setShowQuickReplies(false);
      inputRef.current?.focus();

      // Refresh stats
      loadStats();
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Could not send message',
        status: 'error',
        duration: 2000,
      });
    } finally {
      setSending(false);
    }
  };

  // ============================================================================
  // UPDATE STATUS
  // ============================================================================

  const handleStatusChange = async (status: TicketStatus) => {
    if (!selectedTicket) return;

    try {
      await supportService.updateTicketStatus(selectedTicket.id, status, adminName);
      toast({
        title: 'Status updated',
        status: 'success',
        duration: 2000,
      });
      loadStats();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error',
        description: 'Could not update status',
        status: 'error',
        duration: 2000,
      });
    }
  };

  // ============================================================================
  // UPDATE PRIORITY
  // ============================================================================

  const handlePriorityChange = async (priority: TicketPriority) => {
    if (!selectedTicket) return;

    try {
      await supportService.updateTicketPriority(selectedTicket.id, priority);
      toast({
        title: 'Priority updated',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  // ============================================================================
  // FILTER TICKETS
  // ============================================================================

  const filteredTickets = tickets.filter(ticket => {
    if (statusFilter && ticket.status !== statusFilter) return false;
    if (priorityFilter && ticket.priority !== priorityFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.username.toLowerCase().includes(query) ||
        ticket.email.toLowerCase().includes(query) ||
        ticket.subject.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // ============================================================================
  // HELPERS
  // ============================================================================

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  const formatMessageTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status: string) => {
    return TICKET_STATUSES.find(s => s.id === status)?.color || 'gray';
  };

  const getPriorityColor = (priority: string) => {
    return TICKET_PRIORITIES.find(p => p.id === priority)?.color || 'gray';
  };

  // ============================================================================
  // RENDER: STATS
  // ============================================================================

  const renderStats = () => (
    <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4} mb={6}>
      <PokerCard>
        <Stat>
          <StatLabel color="gray.400">Open</StatLabel>
          <StatNumber color="blue.400">{stats?.open || 0}</StatNumber>
        </Stat>
      </PokerCard>
      <PokerCard>
        <Stat>
          <StatLabel color="gray.400">In Progress</StatLabel>
          <StatNumber color="yellow.400">{stats?.inProgress || 0}</StatNumber>
        </Stat>
      </PokerCard>
      <PokerCard>
        <Stat>
          <StatLabel color="gray.400">Waiting</StatLabel>
          <StatNumber color="purple.400">{stats?.waitingUser || 0}</StatNumber>
        </Stat>
      </PokerCard>
      <PokerCard>
        <Stat>
          <StatLabel color="gray.400">Resolved</StatLabel>
          <StatNumber color="green.400">{stats?.resolved || 0}</StatNumber>
        </Stat>
      </PokerCard>
      <PokerCard>
        <Stat>
          <StatLabel color="gray.400">Urgent</StatLabel>
          <StatNumber color="red.400">{stats?.urgent || 0}</StatNumber>
        </Stat>
      </PokerCard>
      <PokerCard>
        <Stat>
          <StatLabel color="gray.400">Unread</StatLabel>
          <StatNumber color="orange.400">{stats?.unreadTotal || 0}</StatNumber>
        </Stat>
      </PokerCard>
    </SimpleGrid>
  );

  // ============================================================================
  // RENDER: TICKET LIST
  // ============================================================================

  const renderTicketList = () => (
    <Box
      w={{ base: '100%', lg: '400px' }}
      h="calc(100vh - 400px)"
      minH="500px"
      bg="#0D1B2A"
      borderRadius="xl"
      overflow="hidden"
      display={{ base: mobileView === 'list' ? 'block' : 'none', lg: 'block' }}
    >
      {/* Filters */}
      <Box p={4} borderBottom="1px solid" borderColor="gray.800">
        <HStack spacing={2} mb={3}>
          <Input
            placeholder="Search..."
            size="sm"
            bg="#1A2E3B"
            border="none"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <IconButton
            aria-label="Refresh"
            icon={<RefreshCw size={16} />}
            size="sm"
            onClick={loadStats}
          />
        </HStack>
        <HStack spacing={2}>
          <Select
            size="sm"
            bg="#1A2E3B"
            border="none"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as TicketStatus | '')}
          >
            <option value="">All Status</option>
            {TICKET_STATUSES.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </Select>
          <Select
            size="sm"
            bg="#1A2E3B"
            border="none"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as TicketPriority | '')}
          >
            <option value="">All Priority</option>
            {TICKET_PRIORITIES.map(p => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </Select>
        </HStack>
      </Box>

      {/* Ticket List */}
      <Box overflowY="auto" h="calc(100% - 120px)">
        {loading ? (
          <VStack py={10}>
            <Spinner color="cyan.400" />
          </VStack>
        ) : filteredTickets.length === 0 ? (
          <VStack py={10}>
            <Text color="gray.500">No tickets found</Text>
          </VStack>
        ) : (
          <VStack spacing={0} align="stretch">
            {filteredTickets.map(ticket => (
              <Box
                key={ticket.id}
                p={4}
                cursor="pointer"
                bg={selectedTicket?.id === ticket.id ? '#1A2E3B' : 'transparent'}
                borderBottom="1px solid"
                borderColor="gray.800"
                _hover={{ bg: '#1A2E3B' }}
                onClick={() => {
                  setSelectedTicket(ticket);
                  setMobileView('chat');
                }}
              >
                <HStack justify="space-between" mb={1}>
                  <HStack spacing={2}>
                    <Text fontSize="sm">
                      {TICKET_CATEGORIES.find(c => c.id === ticket.category)?.icon}
                    </Text>
                    <Text color="white" fontSize="sm" fontWeight="medium" noOfLines={1}>
                      {ticket.username}
                    </Text>
                    {ticket.unreadByAdmin > 0 && (
                      <Badge colorScheme="red" borderRadius="full" fontSize="10px">
                        {ticket.unreadByAdmin}
                      </Badge>
                    )}
                  </HStack>
                  <Text color="gray.600" fontSize="xs">
                    {formatTime(ticket.updatedAt)}
                  </Text>
                </HStack>
                <Text color="gray.400" fontSize="xs" noOfLines={1} mb={2}>
                  {ticket.subject}
                </Text>
                <HStack spacing={2}>
                  <Badge colorScheme={getStatusColor(ticket.status)} fontSize="9px">
                    {TICKET_STATUSES.find(s => s.id === ticket.status)?.label}
                  </Badge>
                  <Badge colorScheme={getPriorityColor(ticket.priority)} fontSize="9px">
                    {ticket.priority}
                  </Badge>
                </HStack>
              </Box>
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );

  // ============================================================================
  // RENDER: CHAT VIEW
  // ============================================================================

  const renderChatView = () => (
    <Box
      flex={1}
      h="calc(100vh - 400px)"
      minH="500px"
      bg="#0D1B2A"
      borderRadius="xl"
      overflow="hidden"
      display={{ base: mobileView === 'chat' ? 'flex' : 'none', lg: 'flex' }}
      flexDirection="column"
    >
      {!selectedTicket ? (
        <VStack justify="center" h="100%" color="gray.500">
          <MessageCircle size={48} />
          <Text>Select a ticket to view</Text>
        </VStack>
      ) : (
        <>
          {/* Header */}
          <HStack p={4} borderBottom="1px solid" borderColor="gray.800" justify="space-between">
            <HStack spacing={3}>
              <IconButton
                aria-label="Back"
                icon={<ArrowLeft size={18} />}
                size="sm"
                display={{ base: 'flex', lg: 'none' }}
                onClick={() => setMobileView('list')}
              />
              <Avatar size="sm" name={selectedTicket.username} />
              <Box>
                <Text color="white" fontSize="sm" fontWeight="medium">
                  {selectedTicket.username}
                </Text>
                <Text color="gray.500" fontSize="xs">
                  {selectedTicket.email}
                </Text>
              </Box>
            </HStack>
            <HStack spacing={2}>
              <Select
                size="sm"
                w="130px"
                bg="#1A2E3B"
                border="none"
                value={selectedTicket.status}
                onChange={(e) => handleStatusChange(e.target.value as TicketStatus)}
              >
                {TICKET_STATUSES.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </Select>
              <Select
                size="sm"
                w="100px"
                bg="#1A2E3B"
                border="none"
                value={selectedTicket.priority}
                onChange={(e) => handlePriorityChange(e.target.value as TicketPriority)}
              >
                {TICKET_PRIORITIES.map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </Select>
            </HStack>
          </HStack>

          {/* Messages */}
          <Box flex={1} overflowY="auto" p={4}>
            <VStack spacing={3} align="stretch">
              {messages.map(msg => (
                <Box
                  key={msg.id}
                  maxW="80%"
                  p={3}
                  borderRadius="lg"
                  ml={msg.isAdmin ? 'auto' : 0}
                  mr={msg.isAdmin ? 0 : 'auto'}
                  bg={msg.isSystem ? 'transparent' : msg.isAdmin ? POKER_COLORS.cyan + '33' : '#2D2D2D'}
                  borderBottomRightRadius={msg.isAdmin ? '4px' : 'lg'}
                  borderBottomLeftRadius={msg.isAdmin ? 'lg' : '4px'}
                >
                  {msg.isSystem ? (
                    <Text color="gray.500" fontSize="xs" textAlign="center">
                      {msg.message}
                    </Text>
                  ) : (
                    <>
                      <Text color="white" fontSize="sm">
                        {msg.message}
                      </Text>
                      <Text color="gray.500" fontSize="10px" mt={1}>
                        {msg.isAdmin ? `${msg.senderName} • ` : ''}{formatMessageTime(msg.createdAt)}
                      </Text>
                    </>
                  )}
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </VStack>
          </Box>

          {/* Quick Replies */}
          {showQuickReplies && (
            <Box p={2} borderTop="1px solid" borderColor="gray.800" bg="#1A2E3B">
              <HStack spacing={2} flexWrap="wrap">
                {QUICK_REPLIES.map((reply, i) => (
                  <Badge
                    key={i}
                    cursor="pointer"
                    px={3}
                    py={1}
                    borderRadius="full"
                    colorScheme="cyan"
                    variant="outline"
                    onClick={() => {
                      setNewMessage(reply.message);
                      setShowQuickReplies(false);
                      inputRef.current?.focus();
                    }}
                  >
                    {reply.label}
                  </Badge>
                ))}
              </HStack>
            </Box>
          )}

          {/* Input */}
          <Box p={4} borderTop="1px solid" borderColor="gray.800">
            <HStack spacing={2}>
              <Tooltip label="Quick Replies">
                <IconButton
                  aria-label="Quick replies"
                  icon={<MessageCircle size={18} />}
                  size="sm"
                  variant={showQuickReplies ? 'solid' : 'ghost'}
                  colorScheme="cyan"
                  onClick={() => setShowQuickReplies(!showQuickReplies)}
                />
              </Tooltip>
              <Textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your response..."
                bg="#1A2E3B"
                border="none"
                resize="none"
                rows={2}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <IconButton
                aria-label="Send"
                icon={sending ? <Spinner size="sm" /> : <Send size={18} />}
                colorScheme="cyan"
                isDisabled={!newMessage.trim() || sending}
                onClick={handleSendMessage}
              />
            </HStack>
          </Box>
        </>
      )}
    </Box>
  );

  // ============================================================================
  // RENDER: USER INFO SIDEBAR
  // ============================================================================

  const renderUserInfo = () => (
    <Box
      w="280px"
      h="calc(100vh - 400px)"
      minH="500px"
      bg="#0D1B2A"
      borderRadius="xl"
      overflow="hidden"
      display={{ base: 'none', xl: 'block' }}
    >
      {selectedTicket ? (
        <VStack p={4} spacing={4} align="stretch">
          <VStack>
            <Avatar size="lg" name={selectedTicket.username} />
            <Text color="white" fontWeight="medium">{selectedTicket.username}</Text>
            <Text color="gray.500" fontSize="sm">{selectedTicket.email}</Text>
          </VStack>

          <Divider borderColor="gray.700" />

          <Box>
            <Text color="gray.500" fontSize="xs" mb={2}>TICKET INFO</Text>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Text color="gray.400" fontSize="sm">Category</Text>
                <Text color="white" fontSize="sm">
                  {TICKET_CATEGORIES.find(c => c.id === selectedTicket.category)?.label}
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text color="gray.400" fontSize="sm">Created</Text>
                <Text color="white" fontSize="sm">
                  {selectedTicket.createdAt.toLocaleDateString()}
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text color="gray.400" fontSize="sm">Email Updates</Text>
                <Badge colorScheme={selectedTicket.emailUpdates ? 'green' : 'gray'}>
                  {selectedTicket.emailUpdates ? 'On' : 'Off'}
                </Badge>
              </HStack>
              <HStack justify="space-between">
                <Text color="gray.400" fontSize="sm">Moved to Email</Text>
                <Badge colorScheme={selectedTicket.movedToEmail ? 'blue' : 'gray'}>
                  {selectedTicket.movedToEmail ? 'Yes' : 'No'}
                </Badge>
              </HStack>
            </VStack>
          </Box>

          <Divider borderColor="gray.700" />

          <Box>
            <Text color="gray.500" fontSize="xs" mb={2}>QUICK ACTIONS</Text>
            <VStack align="stretch" spacing={2}>
              <HStack
                p={2}
                borderRadius="md"
                cursor="pointer"
                _hover={{ bg: '#1A2E3B' }}
                onClick={() => handleStatusChange('resolved')}
              >
                <CheckCircle size={16} color="#22c55e" />
                <Text color="gray.300" fontSize="sm">Mark Resolved</Text>
              </HStack>
              <HStack
                p={2}
                borderRadius="md"
                cursor="pointer"
                _hover={{ bg: '#1A2E3B' }}
                onClick={() => handlePriorityChange('urgent')}
              >
                <AlertTriangle size={16} color="#ef4444" />
                <Text color="gray.300" fontSize="sm">Mark Urgent</Text>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      ) : (
        <VStack justify="center" h="100%" color="gray.500">
          <User size={32} />
          <Text fontSize="sm">Select a ticket</Text>
        </VStack>
      )}
    </Box>
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Box>
      {renderStats()}

      <HStack spacing={4} align="stretch">
        {renderTicketList()}
        {renderChatView()}
        {renderUserInfo()}
      </HStack>
    </Box>
  );
};

export default SupportAdminTab;